import React, { useContext, useState } from 'react';
import { Image, StyleProp, ViewStyle } from 'react-native';
import { View } from '@shared/components/View';
import { FileUpload } from '@shared/components/blocks/types';
import { MBIconV2 } from '@shared/components/image';
import { MBLoadingIndicator } from '@shared/components/loading';
import { MBTouchableOpacity } from '@shared/components/touchable';
import { useSelector } from '@shared/store';
import { ColorThemeContext, grid } from '@shared/styles';
import { useImageSrcAndDimensions, useMountedRef } from '@shared/util/hooks';
import { CardRenderContext } from '../../../contexts/render';
import { CardThemeContext } from '../../../contexts/theme';

interface Props {
  image: FileUpload;
  marginLeft: number;
  marginRight: number;
  editable: boolean;
  style?: StyleProp<ViewStyle>;
  onClear?(): void;
  onClick?(image: FileUpload): void;
  maxHeight?: number;
  maxWidth?: number;
  minWidth: number;
}

export function ImageListItem({
  image,
  marginLeft,
  marginRight,
  editable,
  onClear,
  onClick,
  maxWidth,
  maxHeight,
  minWidth,
}: Props) {
  const colors = useContext(ColorThemeContext);
  const renderContext = useContext(CardRenderContext);
  const stale = useContext(CardThemeContext) === 'stale';
  const [imgLoaded, setImgLoaded] = useState<boolean>(false);
  const mounted = useMountedRef();
  const { viewportWidth, viewportHeight } = useSelector(state => state.appState);
  const maxW = maxWidth ?? viewportWidth;
  const maxH = maxHeight ?? viewportHeight;

  const { src, width, height, ratio } = useImageSrcAndDimensions({
    image,
    containerWidth: maxW,
    containerHeight: maxH,
    grayscale: stale,
    size: 'contain',
  });

  const isClickable = renderContext === 'form' || renderContext === 'detail';
  const handlePress = () => onClick?.(image);

  const onLoad = () => {
    if (mounted.current) {
      setImgLoaded(true);
    }
  };

  if (!width || !height) {
    return null;
  }

  return (
    <MBTouchableOpacity
      onPress={isClickable ? handlePress : undefined}
      style={{ marginLeft, marginRight }}
    >
      <View
        style={{
          minWidth,
          width: maxH * ratio,
          maxWidth,
          height: maxHeight,
          backgroundColor: colors.dropdown.backgroundNoSelection,
          borderRadius: grid(1),
          overflow: 'hidden',
          justifyContent: 'center',
        }}
        centerHorizontally
      >
        <Image
          source={{ uri: src }}
          onLoad={onLoad}
          style={{ height, width }}
          resizeMode={'contain'}
        />
        {!imgLoaded && <MBLoadingIndicator style={{ paddingTop: maxH / 2 }} />}
      </View>
      {editable && imgLoaded && (
        <View absolute style={{ alignItems: 'flex-end' }}>
          <MBTouchableOpacity onPress={onClear} style={{ padding: 4 }}>
            <View
              center
              style={{
                borderRadius: 12,
                height: 24,
                width: 24,
                backgroundColor: colors.foregroundActive,
                borderColor: colors.background,
                borderWidth: 2,
              }}
            >
              <MBIconV2 name="exit-no-border" color={colors.background} size={12} />
            </View>
          </MBTouchableOpacity>
        </View>
      )}
    </MBTouchableOpacity>
  );
}
